
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  layout: 'reference',
  section: 'Signal and Balance',
  title: 'Reference',
  source: 'endpoint',
  description: 'API reference for Signal Transaction Scores and Balance endpoints'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Schema = makeShortcode("Schema");
const MDXCodeBlock = makeShortcode("MDXCodeBlock");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title='Signal Transaction Scores and Balance endpoints' subtitle='API Reference guide for the Signal Transaction Scores and Balance products' mdxType="Header" />
    <p>{`For how-to guidance, see the `}<a parentName="p" {...{
        "href": "/docs/balance"
      }}>{`Balance documentation`}</a>{` and `}<a parentName="p" {...{
        "href": "/docs/signal"
      }}>{`Signal Transaction Scores documentation`}</a>{`.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Plaid Signal endpoints (for both Balance and Signal Transaction Scores)`}</th>
          <th parentName="tr" {...{
            "align": null
          }}></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/signal/evaluate`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Evaluate a proposed ACH transaction for return risk`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/signal/decision/report`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Report whether you initiated an ACH transaction`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/signal/return/report`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Report a return for an ACH transaction`}</td>
        </tr>
      </tbody>
    </table>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Signal Transaction Scores-only endpoints`}</th>
          <th parentName="tr" {...{
            "align": null
          }}></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/signal/prepare`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Enable an existing Item for Signal Transaction Scores`}</td>
        </tr>
      </tbody>
    </table>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Balance-only endpoints`}</th>
          <th parentName="tr" {...{
            "align": null
          }}></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/accounts/balance/get`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Fetch real-time balances`}</td>
        </tr>
      </tbody>
    </table>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`See also`}</th>
          <th parentName="tr" {...{
            "align": null
          }}></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/processor/token/create`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Create a token for using Plaid Signal Transaction Scores with a processor partner`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/processor/signal/evaluate`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Evaluate a proposed ACH transaction for return risk as a processor partner`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/processor/signal/decision/report`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Report whether you initiated an ACH transaction as a processor partner`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/processor/signal/return/report`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Report a return for an ACH transaction as a processor partner`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/processor/signal/prepare`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Enable an existing processor token for Signal Transaction Scores`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/processor/balance/get`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Fetch real-time balances as a processor partner`}</td>
        </tr>
      </tbody>
    </table>
    <h4 {...{
      "id": "signalevaluate"
    }}>{`/signal/evaluate`}</h4>
    <Schema method='POST' route='/signal/evaluate' type='request' mdxType="Schema">
      <MDXCodeBlock type='server' title='/signal/evaluate' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const eval_request = {
  access_token: 'access-sandbox-71e02f71-0960-4a27-abd2-5631e04f2175',
  account_id: '3gE5gnRzNyfXpBK5wEEKcymJ5albGVUqg77gr',
  client_transaction_id: 'txn12345',
  amount: 123.45,
  client_user_id: 'user1234',
  user: {
    name: {
      prefix: 'Ms.',
      given_name: 'Jane',
      middle_name: 'Leah',
      family_name: 'Doe',
      suffix: 'Jr.',
    },
    phone_number: '+14152223333',
    email_address: 'jane.doe@example.com',
    address: {
      street: '2493 Leisure Lane',
      city: 'San Matias',
      region: 'CA',
      postal_code: '93405-2255',
      country: 'US',
    },
  },
  device: {
    ip_address: '198.30.2.2',
    user_agent:
      'Mozilla/5.0 (iPhone; CPU iPhone OS 13_5_1 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/13.1.1 Mobile/15E148 Safari/604.1',
  },
};

try {
  const eval_response = await plaidClient.signalEvaluate(eval_request);
  const core_attributes = eval_response.data.core_attributes;
  const scores = eval_response.data.scores;
} catch (error) {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/signal/evaluate \\
-H 'Content-Type: application/json' \\
-d '{
   "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
   "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
   "access_token": "access-sandbox-71e02f71-0960-4a27-abd2-5631e04f2175",
   "account_id": "3gE5gnRzNyfXpBK5wEEKcymJ5albGVUqg77gr",
   "client_transaction_id": "txn12345",
   "amount": 123.45,
   "ruleset_key": "normal_risk"
  }'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`request = Plaid::SignalEvaluateRequest.new(
  {
    access_token: access_token,
    account_id: '3gE5gnRzNyfXpBK5wEEKcymJ5albGVUqg77gr',
    client_transaction_id: 'txn12345',
    amount: 123.45,
    ruleset_key: "normal_risk"
  }
)
response = client.signal_evaluate(request)
result = response.ruleset.result
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`SignalEvaluateRequest request = new SignalEvaluateRequest()
  .accessToken("access-sandbox-71e02f71-0960-4a27-abd2-5631e04f2175")
  .accountId("3gE5gnRzNyfXpBK5wEEKcymJ5albGVUqg77gr")
  .clientTransactionId("txn12345")
  .amount(123.45)
  .rulesetKey("normal_risk");

Response<SignalEvaluateResponse> response = plaidClient()
  .signalEvaluate(request)
  .execute();
RuleResult result = response.body().getRuleset().getResult();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = SignalEvaluateRequest(
  access_token="access-sandbox-71e02f71-0960-4a27-abd2-5631e04f2175",
  account_id="3gE5gnRzNyfXpBK5wEEKcymJ5albGVUqg77gr",
  client_transaction_id="txn12345",
  amount=123.45,
  ruleset_key="normal_risk"
)
response = client.signal_evaluate(request)
result = response['ruleset']['result']
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`request := plaid.NewSignalEvaluateRequest(
  "access-sandbox-71e02f71-0960-4a27-abd2-5631e04f2175",
  "3gE5gnRzNyfXpBK5wEEKcymJ5albGVUqg77gr",
  "txn12345",
  123.45,
)
request.SetRulesetKey("normal_risk")
response, _, err := client.PlaidApi.SignalEvaluate(ctx).SignalEvaluateRequest(*request).Execute()
ruleset := response.GetRuleset()
result := ruleset.GetResult()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`request := &plaid.SignalEvaluateRequest{
    AccessToken:         "access-sandbox-71e02f71-0960-4a27-abd2-5631e04f2175",
    AccountID:           "3gE5gnRzNyfXpBK5wEEKcymJ5albGVUqg77gr",
    ClientTransactionID: "txn12345",
    Amount:              123.45,
}
request.RulesetKey = plaid.String("normal_risk")
response, err := client.Signal.Evaluate(ctx, request)
ruleset := response.GetRuleset()
result := ruleset.GetResult()
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/signal/evaluate' type='response' mdxType="Schema" />
    <h4 {...{
      "id": "signaldecisionreport"
    }}>{`/signal/decision/report`}</h4>
    <Schema method='POST' route='/signal/decision/report' type='request' mdxType="Schema">
      <MDXCodeBlock type='server' title='/signal/decision/report' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const decision_report_request = {
  client_transaction_id: 'txn12345',
  initiated: true,
  days_funds_on_hold: 3,
};

try {
  const decision_report_response = await plaidClient.signalDecisionReport(
    decision_report_request,
  );
  const decision_request_id = decision_report_response.data.request_id;
} catch (error) {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/signal/decision/report \\
-H 'Content-Type: application/json' \\
 -d '{
   "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
   "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
   "client_transaction_id": "txn12345",
   "initiated": true
 }'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`request = Plaid::SignalDecisionReportRequest.new(
  {
    client_transaction_id: 'txn12345',
    initiated: true,
    days_funds_on_hold: 3
  }
)
response = client.signal_decision_report(request)
request_id = response.request_id
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`SignalDecisionReportRequest request = new SignalDecisionReportRequest()
  .clientTransactionId("txn12345")
  .initiated(true)
  .daysFundsOnHold(3);

Response<SignalDecisionReportResponse> response = plaidClient()
  .signalDecisionReport(request)
  .execute();
String requestID = response.body().getRequestId();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = SignalDecisionReportRequest(
  client_transaction_id="txn12345",
  initiated=True,
  days_funds_on_hold=3,
)
response = client.signal_decision_report(request)
request_id = response['request_id']
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`request := plaid.NewSignalDecisionReportRequest(
  "txn12345",
  true,
)
request.SetDaysFundsOnHold(3)
response, _, err := client.PlaidApi.SignalDecisionReport(ctx).SignalDecisionReportRequest(*request).Execute()
request_id := response.GetRequestId()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`request := &plaid.SignalDecisionReportRequest{
    ClientTransactionID: "txn12345",
    Initiated:           true,
}
request.DaysFundsOnHold = plaid.Int(3)
response, err := client.Signal.ReportDecision(ctx, request)
request_id := response.GetRequestID()
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/signal/decision/report' type='response' mdxType="Schema" />
    <h4 {...{
      "id": "signalreturnreport"
    }}>{`/signal/return/report`}</h4>
    <Schema method='POST' route='/signal/return/report' type='request' mdxType="Schema">
      <MDXCodeBlock type='server' title='/signal/return/report' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const return_report_request = {
  client_transaction_id: 'txn12345',
  return_code: 'R01',
};

try {
  const return_report_response = await plaidClient.signalReturnReport(
    return_report_request,
  );
  const request_id = return_report_response.data.request_id;
  console.log(request_id);
} catch (error) {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/signal/return/report \\
-H 'Content-Type: application/json' \\
-d '{
   "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
   "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
   "client_transaction_id": "txn12345",
   "return_code": "R01"
 }'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`request = Plaid::SignalReturnReportRequest.new(
  {
    client_transaction_id: 'txn12345',
    return_code: 'R01'
  }
)
response = client.signal_return_report(request)
request_id = response.request_id
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`SignalReturnReportRequest request = new SignalReturnReportRequest()
  .clientTransactionId("txn12345")
  .returnCode("R01");

Response<SignalReturnReportResponse> response = client()
  .signalReturnReport(request)
  .execute();

String requestId = response.body().getRequestId();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = SignalReturnReportRequest(
  client_transaction_id="txn12345",
  return_code="R01",
)
response = client.signal_return_report(request)
request_id = response['request_id']
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`request := plaid.NewSignalReturnReportRequest(
  "txn12345",
  "R01",
)
response, _, err := client.PlaidApi.SignalReturnReport(ctx).SignalReturnReportRequest(*request).Execute()
request_id := response.GetRequestId()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`request := &plaid.SignalReturnReportRequest{
    ClientTransactionID: "txn12345",
    ReturnCode:          "R01",
}
response, err := client.Signal.ReportReturn(ctx, request)
request_id := response.GetRequestID()
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/signal/return/report' type='response' mdxType="Schema" />
    <h4 {...{
      "id": "signalprepare"
    }}>{`/signal/prepare`}</h4>
    <Schema method='POST' route='/signal/prepare' type='request' mdxType="Schema">
      <MDXCodeBlock type='server' title='/signal/prepare' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const prepare_request = {
  access_token: 'access-sandbox-71e02f71-0960-4a27-abd2-5631e04f2175',
};

try {
  const prepare_response = await plaidClient.signalPrepare(prepare_request);
  const request_id = prepare_response.data.request_id;
  console.log(request_id);
} catch (error) {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/signal/prepare \\
-H 'Content-Type: application/json' \\
-d '{
   "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
   "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
   "access_token": "access-sandbox-71e02f71-0960-4a27-abd2-5631e04f2175"
 }'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`request = Plaid::SignalPrepareRequest.new(
  {
    access_token: 'access-sandbox-71e02f71-0960-4a27-abd2-5631e04f2175'
  }
)
response = client.signal_prepare(request)
request_id = response.request_id
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`SignalPrepareRequest request = new SignalPrepareRequest()
  .accessToken("access-sandbox-71e02f71-0960-4a27-abd2-5631e04f2175");

Response<SignalPrepareResponse> response = client()
  .signalPrepare(request)
  .execute();

String requestId = response.body().getRequestId();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = SignalPrepareRequest(
  access_token="access-sandbox-71e02f71-0960-4a27-abd2-5631e04f2175",
)
response = client.signal_prepare(request)
request_id = response['request_id']
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`request := plaid.NewSignalPrepareRequest(
  "access-sandbox-71e02f71-0960-4a27-abd2-5631e04f2175",
)
response, _, err := client.PlaidApi.SignalPrepare(ctx).SignalPrepareRequest(*request).Execute()
request_id := response.GetRequestId()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`request := &plaid.SignalPrepareRequest{AccessToken: "access-sandbox-71e02f71-0960-4a27-abd2-5631e04f2175"}
response, err := client.Signal.Prepare(ctx, request)
request_id := response.GetRequestID()
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/signal/prepare' type='response' mdxType="Schema" />
    <h4 {...{
      "id": "accountsbalanceget"
    }}>{`/accounts/balance/get`}</h4>
    <Schema method='POST' route='/accounts/balance/get' type='request' mdxType="Schema">
      <MDXCodeBlock type='server' title='/accounts/balance/get' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`// Pull real-time balance information for each account associated
// with the Item
const request: AccountsBalanceGetRequest = {
  access_token: accessToken,
};
try {
  const response = await plaidClient.accountsBalanceGet(request);
  const accounts = response.data.accounts;
} catch (error) {
  // handle error
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/accounts/balance/get \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "access_token": String,
  "options": {
    "account_ids": [String]
  }
}'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`# Pull real-time balance information for each account associated
# with the Item
request = Plaid::AccountsBalanceGetRequest.new({ access_token: access_token })
response = client.accounts_balance_get(request)
accounts = response.accounts
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`// Pull real-time balance information for each account associated
// with the Item
AccountsBalanceGetRequest request = new AccountsBalanceGetRequest()
  .accessToken(accessToken);
Response<AccountsGetResponse> response = client()
  .accountsBalanceGet(request)
  .execute();
List<AccountBase> accounts = response.body().getAccounts();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`# Pull real-time balance information for each account associated
# with the Item
request = AccountsBalanceGetRequest(access_token=access_token)
response = client.accounts_balance_get(request)
accounts = response['accounts']
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`balancesGetReq := plaid.NewAccountsBalanceGetRequest(accessToken)

balancesGetResp, _, err := client.PlaidApi.AccountsBalanceGet(ctx).AccountsBalanceGetRequest(
  *balancesGetReq,
).Execute()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`balancesGetReq := &plaid.AccountsBalanceGetRequest{AccessToken: accessToken}

balancesGetResp, err := client.Accounts.GetBalance(ctx, balancesGetReq)
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/accounts/balance/get' type='response' mdxType="Schema" />
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "signalevaluate",
        level: 4,
        title: "/signal/evaluate",
      },
{
        id: "signaldecisionreport",
        level: 4,
        title: "/signal/decision/report",
      },
{
        id: "signalreturnreport",
        level: 4,
        title: "/signal/return/report",
      },
{
        id: "signalprepare",
        level: 4,
        title: "/signal/prepare",
      },
{
        id: "accountsbalanceget",
        level: 4,
        title: "/accounts/balance/get",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
